import React from 'react';
import Immutable from 'immutable';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { Paragraph } from '@uncinc/react-drupal-core';
import { ImmutableRootState } from '@uncinc/react-drupal-core/types';

import { LinePart } from '../../../components';
import useClasses from '../../../hooks/useClasses';
import { ImmutableParagraphPhaseEntity } from '..';
import UnlockDate from './UnlockDate';

import './style.scss';

interface Props {
  entity: ImmutableParagraphPhaseEntity;
  className?: string;
  phaseIndex: number;
  currentActivePhaseIndex: number;
  phaseIsActive: boolean;
  unlockDate?: Date;
  unlockInfo?: string;
}

interface Submissions {
  [key: string]: Object[]
}

const ParagraphPhase: React.FC<Props> = (props) => {
  const {
    entity,
    className,
    phaseIsActive,
    currentActivePhaseIndex,
    phaseIndex,
    unlockDate,
    unlockInfo,
  } = props;

  const { t } = useTranslation();
  const { buildClass, joinClasses } = useClasses('ParagraphPhase');
  const title = entity.getIn(['field_title', 0, 'value'], '');
  const exercises = entity.get('field_exercise', Immutable.List());
  const exerciseSubmissions = useSelector<ImmutableRootState<Submissions>, Submissions>((state) => state.getIn(['exerciseSubmissions', 'submissions'], {}));

  let phaseProgress: number = null;
  for (let i = 0; i < exercises.count(); i++) {
    const webformId: string = String(exercises.getIn([i, 'entity', 'field_webform', 0, 'target_id']));
    const submission = exerciseSubmissions[webformId];

    // We stop at the first missing submission.
    if (!submission) {
      phaseProgress = i - 1;
      break;
    }
  }

  let isPhaseComplete = true;

  for (let i = 0; i < exercises.count(); i++) {
    const exercise = exercises.get(i);
    const webformId = exercise.getIn(['entity', 'field_webform', 0, 'target_id'], null);
    const isExerciseComplete = !!exerciseSubmissions[webformId as string];

    if (!isExerciseComplete) {
      isPhaseComplete = false;
      break;
    }
  }

  const noDoneExercise = phaseProgress === -1;
  const isPhaseUnlocked = phaseIsActive || phaseIndex < currentActivePhaseIndex;

  return (
    <div className={joinClasses(className, buildClass())}>
      <div className={buildClass('content')}>
        <div className={buildClass('header', {
          isPhaseUnlocked,
        })}
        >
          <LinePart exerciseStatus={isPhaseUnlocked ? 'done' : 'start'} className={noDoneExercise ? 'LinePart--noDoneExercises' : ''} />
          <h2 className={buildClass('start')}>{t('paragraphs.phase.start', { phase: title })}</h2>

          {unlockDate.getTime() > new Date(new Date().setHours(0, 0, 0, 0)).getTime() && (
            <UnlockDate unlockDate={unlockDate} info={unlockInfo} />
          )}
        </div>

        <div className={buildClass('dotted-line')} />

        {exercises.size > 0 && (
          <ul className={buildClass('exercises')}>
            {exercises.map((exercise, index) => {
              const webformId = exercise.getIn(['entity', 'field_webform', 0, 'target_id'], null);
              const isExerciseComplete = !!exerciseSubmissions[webformId as string];
              let exerciseStatus: string;

              if (phaseIndex < currentActivePhaseIndex) {
                exerciseStatus = 'done';
              } else if (phaseIsActive === false) {
                exerciseStatus = 'locked';
              } else if (index === phaseProgress) {
                exerciseStatus = 'lastExerciseDone';
              } else if (isExerciseComplete) {
                exerciseStatus = 'done';
              } else if (index === phaseProgress + 1) {
                exerciseStatus = 'available';
              } else {
                exerciseStatus = 'locked';
              }

              const id = exercise.get('target_id');
              return (
                <li
                  aria-current={exerciseStatus === 'available'}
                  key={`exercise-list-item-${id}`}
                >
                  <Paragraph
                    key={`paragraph:${id}`}
                    id={id}
                    entity={exercise.get('entity')}
                    exerciseStatus={exerciseStatus}
                  />
                </li>
              );
            })}
          </ul>
        )}

        <footer className={buildClass('footer', {
          done: isPhaseComplete,
        })}
        >
          <LinePart exerciseStatus={isPhaseComplete ? 'done' : 'end'} />
          <h2 className={buildClass('end')}>{t('paragraphs.phase.end', { phase: title })}</h2>
        </footer>
      </div>
    </div>
  );
};

export default ParagraphPhase;
